import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CheckCircle, XCircle, Pencil, Trash2 } from "lucide-react";

interface Props {
  detail: any;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
  onDelete: () => void;
  approving: boolean;
  rejecting: boolean;
}

export default function RegistrationDetail({ detail, onApprove, onReject, onEdit, onDelete, approving, rejecting }: Props) {
  const { t } = useTranslation();

  function statusBadge(status: string) {
    const map: Record<string, { label: string; cls: string }> = {
      pending: { label: t("regDetail.statusPending"), cls: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
      approved: { label: t("regDetail.statusApproved"), cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
      rejected: { label: t("regDetail.statusRejected"), cls: "bg-destructive/10 text-destructive border-destructive/20" },
    };
    const s = map[status] || map.pending;
    return <span className={cn("text-xs px-2 py-0.5 border", s.cls)}>{s.label}</span>;
  }

  return (
    <div className="border border-border bg-card p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-subheadline">{detail.company_name}</h2>
          <p className="text-caption">{detail.contact_name} · {detail.contact_email}</p>
        </div>
        <div className="flex items-center gap-2">
          {statusBadge(detail.status)}
          <Button variant="quiet" size="icon" onClick={onEdit} className="h-8 w-8" title={t("common.save")}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="quiet" size="icon" onClick={onDelete} className="h-8 w-8 text-destructive hover:text-destructive" title={t("common.close")}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        {detail.legal_name && <div><span className="text-micro block">{t("regDetail.legalName")}</span>{detail.legal_name}</div>}
        {detail.phone && <div><span className="text-micro block">{t("regDetail.phone")}</span>{detail.phone}</div>}
        {detail.website && <div><span className="text-micro block">{t("regDetail.website")}</span>{detail.website}</div>}
        {detail.invoice_email && <div><span className="text-micro block">{t("regDetail.invoiceEmail")}</span>{detail.invoice_email}</div>}
        {detail.address_line1 && <div><span className="text-micro block">{t("regDetail.address")}</span>{detail.address_line1}, {detail.postal_code} {detail.city}</div>}
        {detail.vat_id && <div><span className="text-micro block">{t("regDetail.vatId")}</span>{detail.vat_id}</div>}
        {detail.region && <div><span className="text-micro block">{t("regDetail.mainRegion")}</span>{detail.region}</div>}
      </div>

      {detail.categories?.length > 0 && (
        <div>
          <span className="text-micro block mb-2">{t("regDetail.categories")}</span>
          <div className="flex flex-wrap gap-1.5">
            {detail.categories.map((c: string) => (
              <span key={c} className="text-xs px-2 py-0.5 border border-border bg-secondary/30">{c}</span>
            ))}
          </div>
        </div>
      )}

      {detail.service_regions?.length > 0 && (
        <div>
          <span className="text-micro block mb-2">{t("regDetail.serviceRegions")}</span>
          <div className="flex flex-wrap gap-1.5">
            {detail.service_regions.map((r: string) => (
              <span key={r} className="text-xs px-2 py-0.5 border border-border bg-secondary/30">{r}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 text-sm">
        <div><span className="text-micro block">{t("regDetail.sla")}</span>{detail.sla_tier || "–"} ({detail.sla_response_hours || "–"}h)</div>
        <div><span className="text-micro block">{t("regDetail.weeklyLimit")}</span>{detail.weekly_lead_limit || "–"}</div>
        <div><span className="text-micro block">{t("regDetail.remote")}</span>{detail.remote_supported ? t("common.yes") : t("common.no")}</div>
      </div>

      {detail.reviewer_notes && (
        <div>
          <span className="text-micro block mb-1">{t("regDetail.reviewerNotes")}</span>
          <p className="text-sm p-3 border border-border bg-secondary/10">{detail.reviewer_notes}</p>
        </div>
      )}

      {detail.contacts?.length > 0 && (
        <div>
          <span className="text-micro block mb-2">{t("regDetail.additionalContacts")}</span>
          <div className="space-y-2">
            {detail.contacts.map((c: any, i: number) => (
              <div key={i} className="text-sm p-3 border border-border bg-secondary/10">
                {c.name} {c.role && `(${c.role})`} · {c.email} {c.phone && `· ${c.phone}`}
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.status === "pending" && (
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button variant="commit" onClick={onApprove} disabled={approving} className="gap-2">
            <CheckCircle className="w-4 h-4" />
            {approving ? t("regDetail.approving") : t("regDetail.approveBtn")}
          </Button>
          <Button variant="quiet" onClick={onReject} disabled={rejecting} className="gap-2 text-destructive hover:text-destructive">
            <XCircle className="w-4 h-4" />
            {rejecting ? "..." : t("regDetail.rejectBtn")}
          </Button>
        </div>
      )}

      {detail.status === "approved" && detail.provider_id && (
        <div className="p-4 border border-border bg-emerald-500/5">
          <p className="text-sm text-emerald-600 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {t("regDetail.approvedMsg")}
          </p>
        </div>
      )}
    </div>
  );
}
